/**
 * Custom hook for Supplier Performance state management
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  suppliersAPI,
  SupplierProfile,
  SupplierDashboardStats,
  purchasingAPI,
  qualityAPI,
  performanceSettingsAPI,
  SupplierRatingThreshold,
} from '../../lib/api';
import { SupplierPerformanceFilterValues } from '../../components/suppliers/SupplierPerformanceFilters';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useView } from '../../contexts/ViewContext';
import { usePagination } from '../../hooks';
import { getErrorMessage } from '../../hooks/useFetchData';
import { ModalType, ModalContext } from './types';
import { DEFAULT_FILTERS, MODAL_PAGE_SIZE } from './constants';
import { getGradeInfo, isNARating } from './utils';
import { useQualityMeasureLabel } from '../../lib/qualityLabels';
import * as XLSX from 'xlsx';

export function useSupplierPerformance() {
  const { preferences } = useFormatters();
  const qualityLabel = useQualityMeasureLabel();
  const { getViewQueryParams, viewLevel, selectedGroupId, selectedCompanyId, selectedSiteId, isLoading: viewContextLoading } =
    useView();

  // Core states
  const [suppliers, setSuppliers] = useState<SupplierProfile[]>([]);
  const [stats, setStats] = useState<SupplierDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [ratingThresholds, setRatingThresholds] = useState<SupplierRatingThreshold[]>([]);
  const [isMatrixView, setIsMatrixView] = useState(false);
  const [filters, setFilters] = useState<SupplierPerformanceFilterValues>(DEFAULT_FILTERS);
  const [initialLoad, setInitialLoad] = useState(true);

  // Pagination
  const pagination = usePagination({ pageSize: 20, scrollToTop: true });

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalSubtitle, setModalSubtitle] = useState('');
  const [modalData, setModalData] = useState<object[]>([]);
  const [modalType, setModalType] = useState<ModalType>('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalPage, setModalPage] = useState(1);
  const [modalTotalCount, setModalTotalCount] = useState(0);
  const [modalContext, setModalContext] = useState<ModalContext | null>(null);

  const modalTotalPages = Math.ceil(modalTotalCount / MODAL_PAGE_SIZE);

  // Fetch rating thresholds on mount
  useEffect(() => {
    const fetchThresholds = async () => {
      try {
        const response = await performanceSettingsAPI.getSettings();
        if (response.data.rating_thresholds) {
          const sorted = [...response.data.rating_thresholds].sort(
            (a, b) => a.order - b.order || parseFloat(b.min_score) - parseFloat(a.min_score)
          );
          setRatingThresholds(sorted);
        }
      } catch (err) {
        console.error('Error fetching rating thresholds:', err);
      }
    };
    fetchThresholds();
  }, []);

  // Debounce search input - wait 400ms after user stops typing
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm]);

  // Fetch data when dependencies change
  useEffect(() => {
    if (viewContextLoading) return;
    fetchData();
  }, [
    viewContextLoading,
    pagination.currentPage,
    pagination.pageSize,
    debouncedSearchTerm,
    filters,
    viewLevel,
    selectedGroupId,
    selectedCompanyId,
    selectedSiteId,
    preferences.currency,
  ]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: Record<string, string | number | boolean> = {
        ordering: '-overall_rating',
        page: pagination.currentPage,
        page_size: pagination.pageSize,
        currency: preferences.currency,
      };

      const viewParams = getViewQueryParams();
      if (viewParams['group']) params['group'] = viewParams['group'];
      if (viewParams['company']) params['company'] = viewParams['company'];
      if (viewParams['site']) params['site'] = viewParams['site'];

      if (debouncedSearchTerm) params['search'] = debouncedSearchTerm;
      if (filters.division !== 'all') params['division'] = filters.division;
      if (filters.supplierCode) params['supplier_code__icontains'] = filters.supplierCode;
      if (filters.strategic) params['is_preferred'] = true;
      if (filters.supplierGroup !== 'all') params['company__group'] = filters.supplierGroup;
      if (filters.commodityGroup !== 'all') params['commodity_group'] = filters.commodityGroup;
      if (filters.commodityType !== 'all') params['commodity_type'] = filters.commodityType;
      if (filters.dateFrom) params['date_from'] = filters.dateFrom;
      if (filters.dateTo) params['date_to'] = filters.dateTo;
      if (filters.excludeNARatings) params['exclude_na_ratings'] = 'true';
      if (filters.supplierSite !== 'all') params['company_id'] = filters.supplierSite;
      // CR-4783: Geographic + supplier-type filters
      if (filters.siteCountry && filters.siteCountry !== 'all') params['site_country'] = filters.siteCountry;
      if (filters.supplierCountry && filters.supplierCountry !== 'all') params['supplier_country'] = filters.supplierCountry;
      if (filters.supplierType && filters.supplierType !== 'all') params['supplier_type'] = filters.supplierType;

      const [profilesResponse, statsResponse] = await Promise.all([
        suppliersAPI.getProfiles(params),
        suppliersAPI.getDashboardStats(viewParams),
      ]);

      setSuppliers(profilesResponse.data.results);
      pagination.setFromResponse(profilesResponse.data.count);
      setStats(statsResponse.data);
    } catch (err) {
      // If page is out of range (404), reset to page 1 — the useEffect will refetch
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 && pagination.currentPage > 1) {
        pagination.reset();
        return;
      }
      console.error('Error fetching performance data:', err);
      setError(getErrorMessage(err, 'Failed to load performance data'));
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  // Build common filter params for drill-down API calls
  const buildDrilldownParams = (supplierId: number) => {
    const params: Record<string, string | number | boolean> = { supplier: supplierId };
    if (filters.dateFrom) params['date_from'] = filters.dateFrom;
    if (filters.dateTo) params['date_to'] = filters.dateTo;
    if (filters.supplierGroup && filters.supplierGroup !== 'all') {
      params['company__group'] = filters.supplierGroup;
    }
    if (filters.site && filters.site !== 'all') params['site'] = filters.site;
    if (filters.warehouse && filters.warehouse !== 'all') params['warehouse'] = filters.warehouse;
    return params;
  };

  // PO Drilldown
  const openPOModal = async (supplierId: number, supplierName: string, page: number = 1) => {
    setModalTitle(`Purchase Orders - ${supplierName}`);
    setModalSubtitle('Purchase order details');
    setModalType('po');
    setModalOpen(true);
    setModalLoading(true);
    setModalPage(page);
    setModalContext({ supplierId, supplierName });

    try {
      const params: Record<string, string | number | boolean> = buildDrilldownParams(supplierId);
      params['page'] = page;
      params['page_size'] = MODAL_PAGE_SIZE;
      const response = await purchasingAPI.getOrders(params);
      setModalData(response.data.results || []);
      setModalTotalCount(response.data.count || 0);
    } catch (error) {
      console.error('Error loading PO data:', error);
      setModalData([]);
      setModalTotalCount(0);
    } finally {
      setModalLoading(false);
    }
  };

  // NCR Drilldown
  const openNCRModal = async (
    supplierId: number,
    supplierName: string,
    filterType?: string,
    value?: number,
    page: number = 1
  ) => {
    const filterLabel = filterType ? ` (${filterType})` : '';
    setModalTitle(`Non-Conformance Reports - ${supplierName}${filterLabel}`);
    setModalSubtitle(
      value !== undefined ? `${filterType || 'Parts rejected'}: ${value}` : 'Non-conformance report details'
    );
    setModalType('ncr');
    setModalOpen(true);
    setModalLoading(true);
    setModalPage(page);
    setModalContext({ supplierId, supplierName, filterType, value });

    try {
      const params: Record<string, string | number | boolean> = buildDrilldownParams(supplierId);
      params['page'] = page;
      params['page_size'] = MODAL_PAGE_SIZE;
      const response = await qualityAPI.getNCRs(params);
      setModalData(response.data.results || []);
      setModalTotalCount(response.data.count || 0);
    } catch (error) {
      console.error('Error loading NCR data:', error);
      setModalData([]);
      setModalTotalCount(0);
    } finally {
      setModalLoading(false);
    }
  };

  // GRN Drilldown
  const openGRNModal = async (
    supplierId: number,
    supplierName: string,
    filterType?: string,
    value?: number,
    page: number = 1
  ) => {
    const filterLabel = filterType ? ` (${filterType})` : '';
    setModalTitle(`Goods Receipt Notes - ${supplierName}${filterLabel}`);
    setModalSubtitle(
      value !== undefined ? `${filterType || 'Parts delivered'}: ${value}` : 'Goods receipt details'
    );
    setModalType('grn');
    setModalOpen(true);
    setModalLoading(true);
    setModalPage(page);
    setModalContext({ supplierId, supplierName, filterType, value });

    try {
      const params: Record<string, string | number | boolean> = buildDrilldownParams(supplierId);
      params['page'] = page;
      params['page_size'] = MODAL_PAGE_SIZE;
      const response = await purchasingAPI.getGRNs(params);
      setModalData(response.data.results || []);
      setModalTotalCount(response.data.count || 0);
    } catch (error) {
      console.error('Error loading GRN data:', error);
      setModalData([]);
      setModalTotalCount(0);
    } finally {
      setModalLoading(false);
    }
  };

  // PO Lines Drilldown
  const openPOLineModal = async (
    supplierId: number,
    supplierName: string,
    filterType?: string,
    value?: number,
    additionalFilters?: { status?: string; on_hold?: boolean },
    page: number = 1
  ) => {
    const filterLabel = filterType ? ` (${filterType})` : '';
    setModalTitle(`Purchase Order Lines - ${supplierName}${filterLabel}`);
    setModalSubtitle(value !== undefined ? `${filterType}: ${value}` : 'Purchase order line details');
    setModalType('pol');
    setModalOpen(true);
    setModalLoading(true);
    setModalPage(page);
    setModalContext({ supplierId, supplierName, filterType, value, additionalFilters });

    try {
      const params: Record<string, string | number | boolean> = buildDrilldownParams(supplierId);
      params['page'] = page;
      params['page_size'] = MODAL_PAGE_SIZE;
      if (additionalFilters?.status) params['status'] = additionalFilters.status;
      if (additionalFilters?.on_hold !== undefined) params['on_hold'] = additionalFilters.on_hold;
      const response = await purchasingAPI.getLines(params);
      setModalData(response.data.results || []);
      setModalTotalCount(response.data.count || 0);
    } catch (error) {
      console.error('Error loading PO Line data:', error);
      setModalData([]);
      setModalTotalCount(0);
    } finally {
      setModalLoading(false);
    }
  };

  // Handle modal page change
  const handleModalPageChange = (newPage: number) => {
    if (!modalContext || modalLoading) return;

    switch (modalType) {
      case 'po':
        openPOModal(modalContext.supplierId, modalContext.supplierName, newPage);
        break;
      case 'ncr':
        openNCRModal(
          modalContext.supplierId,
          modalContext.supplierName,
          modalContext.filterType,
          modalContext.value,
          newPage
        );
        break;
      case 'grn':
        openGRNModal(
          modalContext.supplierId,
          modalContext.supplierName,
          modalContext.filterType,
          modalContext.value,
          newPage
        );
        break;
      case 'pol':
        openPOLineModal(
          modalContext.supplierId,
          modalContext.supplierName,
          modalContext.filterType,
          modalContext.value,
          modalContext.additionalFilters,
          newPage
        );
        break;
    }
  };

  // Filter suppliers by rating grade (client-side).
  // N/A exclusion is handled server-side via exclude_na_ratings param to keep
  // pagination counts accurate (avoids page 1 showing fewer rows than page_size).
  const filteredSuppliers = suppliers.filter((supplier) => {
    if (filters.rating === 'all') return true;

    const hasNARating = isNARating(supplier);
    if (filters.rating === 'N/A') return hasNARating;
    if (hasNARating) return false;

    const gradeInfo = getGradeInfo(supplier.combined_score, ratingThresholds);
    return gradeInfo.grade === filters.rating || gradeInfo.label === filters.rating;
  });

  // Reset pagination when filters change
  useEffect(() => {
    pagination.reset();
  }, [debouncedSearchTerm, filters]);

  // Apply filters — receives the pending filters from the filter component and triggers a fetch
  const handleApplyFilters = useCallback((newFilters: SupplierPerformanceFilterValues) => {
    setFilters(newFilters);
    pagination.reset();
    // The useEffect watching [filters, pagination.currentPage] will fire and call fetchData
  }, []);

  // Export to Excel
  const handleExportExcel = useCallback(() => {
    if (filteredSuppliers.length === 0) {
      alert('No data to export');
      return;
    }

    const exportData = filteredSuppliers.map((supplier) => ({
      'Supplier Name': supplier.company_name,
      Warehouse: supplier.warehouse_name || '-',
      'Customer Companies': supplier.customers || '-',
      Rating: isNARating(supplier)
        ? 'N/A'
        : getGradeInfo(supplier.combined_score ?? undefined, ratingThresholds).label,
      'Qty Received': supplier.qty_received || 0,
      'Qty Rejected': supplier.reject_qty || 0,
      [`Customer Liability ${qualityLabel}`]: supplier.customer_liability_ppm?.toFixed(2) || 0,
      [`Supplier Liability ${qualityLabel}`]: supplier.supplier_liability_ppm?.toFixed(2) || 0,
      [`Total ${qualityLabel}`]: supplier.total_ppm?.toFixed(2) || 0,
      'No. of NCRs': supplier.ncr_count || 0,
      'Response Score': supplier.response_score || 0,
      'Total Value': supplier.total_value || 0,
      'Reject Value': supplier.reject_value || 0,
      'Customer Cost of Quality': supplier.customer_cost_of_quality || 0,
      'Supplier Cost of Quality': supplier.supplier_cost_of_quality || 0,
      'Total Cost of Quality': supplier.total_cost_of_quality || 0,
      'PO Lines': supplier.po_line_count || 0,
      OTIF: supplier.otif_count || 0,
      Arrears: supplier.arrears_count || 0,
      'OTIF Score %': supplier.otif_score_percentage?.toFixed(1) || 0,
      'Qty Diff': supplier.qty_diff || 0,
      'No. of GRNs': supplier.grn_count || 0,
      Ontime: supplier.ontime_count || 0,
      Late: supplier.late_count || 0,
      Early: supplier.early_count || 0,
      'CO2 Emissions': supplier.carbon_emission || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Supplier Performance');

    const date = new Date().toISOString().split('T')[0];
    const filename = `Supplier_Performance_${date}.xlsx`;
    XLSX.writeFile(workbook, filename);
  }, [filteredSuppliers, ratingThresholds, qualityLabel]);

  // Toggle matrix view
  const handleMatrixView = useCallback(() => {
    setIsMatrixView(!isMatrixView);
  }, [isMatrixView]);

  return {
    // Data
    suppliers,
    stats,
    filteredSuppliers,
    ratingThresholds,

    // UI states
    loading,
    initialLoad,
    error,
    searchTerm,
    isMatrixView,
    filters,

    // Pagination
    pagination,

    // Modal
    modalOpen,
    modalTitle,
    modalSubtitle,
    modalData,
    modalType,
    modalLoading,
    modalPage,
    modalTotalCount,
    modalTotalPages,

    // Currency
    currency: preferences.currency,

    // Setters
    setSearchTerm,
    setModalOpen,

    // Actions
    fetchData,
    handleApplyFilters,
    handleExportExcel,
    handleMatrixView,
    handleModalPageChange,
    openPOModal,
    openNCRModal,
    openGRNModal,
    openPOLineModal,
  };
}
